import type { DashboardRole } from '../types';

export interface RolePermissions {
  // Projects
  canViewAllWorkspaceProjects: boolean;
  canCreateProject: boolean;
  canAssignAnimator: boolean;
  canAddProjectMembers: boolean;
  canArchiveProject: boolean;

  // Assets
  canUploadAssets: boolean;
  canViewAllWorkspaceAssets: boolean;

  // Messages
  canViewAllWorkspaceThreads: boolean;

  // Settings
  canManageWorkspaceSettings: boolean;
  canInviteTeamMembers: boolean;
  canApproveAccessRequests: boolean;
  canUpdateMemberRoles: boolean;
  canPurchaseManuals: boolean;
  canManageOwnProfile: boolean;

  // Viewer
  canViewManualViewer: boolean;
}

/**
 * Pure function that returns the permission set for a given role.
 * This is the frontend permission source — it controls UI visibility only.
 * Backend authorization is the security boundary.
 */
export function getRolePermissions(role: DashboardRole): RolePermissions {
  const isAdminOrAbove = role === 'admin' || role === 'superadmin';

  return {
    // Projects
    canViewAllWorkspaceProjects: isAdminOrAbove,
    canCreateProject: role === 'admin' || role === 'client' || role === 'superadmin',
    canAssignAnimator: isAdminOrAbove,
    canAddProjectMembers: isAdminOrAbove,
    canArchiveProject: isAdminOrAbove,

    // Assets
    canUploadAssets: true,
    canViewAllWorkspaceAssets: isAdminOrAbove,

    // Messages
    canViewAllWorkspaceThreads: isAdminOrAbove,

    // Settings
    canManageWorkspaceSettings: isAdminOrAbove,
    canInviteTeamMembers: isAdminOrAbove,
    canApproveAccessRequests: isAdminOrAbove,
    canUpdateMemberRoles: isAdminOrAbove,
    canPurchaseManuals: role === 'admin',
    canManageOwnProfile: true,

    // Viewer
    canViewManualViewer: true,
  };
}

/**
 * React hook wrapper for getRolePermissions.
 * Use this in components; use getRolePermissions directly in tests.
 */
export function useRolePermissions(role: DashboardRole): RolePermissions {
  return getRolePermissions(role);
}
